import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, User, Loader2, Trash2, Sparkles, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { chatAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTime } from '../lib/utils';
import { toast } from 'sonner';

const DR_ERIKA_AVATAR = 'https://customer-assets.emergentagent.com/job_rejuven-lang/artifacts/fi2n00l7_Dr.%20Erika.png';

export function ChatBubble() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [lastSeenMessageId, setLastSeenMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load chat history on mount
  useEffect(() => {
    loadChatHistory();
    // Check for proactive message after a short delay
    const timer = setTimeout(checkProactiveMessage, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Check for proactive messages from Dr. Erika
  const checkProactiveMessage = async () => {
    try {
      const response = await chatAPI.getProactiveMessage();
      if (response.data.message) {
        // There's a new proactive message - show notification
        setHasNewMessage(true);
        // Reload chat history to include the proactive message
        await loadChatHistory();
      }
    } catch (error) {
      // Silently fail
    }
  };

  // Check for new messages and proactive messages periodically when chat is closed
  useEffect(() => {
    if (isOpen) return;
    
    const interval = setInterval(async () => {
      try {
        // Check for proactive messages first
        await checkProactiveMessage();
        
        // Then check for any new messages
        const response = await chatAPI.getHistory();
        const msgs = response.data.messages || [];
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg.role === 'assistant' && lastMsg.id !== lastSeenMessageId) {
            setHasNewMessage(true);
          }
        }
      } catch (error) {
        // Silently fail for polling
      }
    }, 60000); // Check every 60 seconds (increased from 30 to reduce load)
    
    return () => clearInterval(interval);
  }, [isOpen, lastSeenMessageId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const loadChatHistory = async () => {
    try {
      const response = await chatAPI.getHistory();
      const msgs = response.data.messages || [];
      setMessages(msgs);
      if (msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1];
        setLastSeenMessageId(lastMsg.id);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOpen = () => {
    setIsOpen(true);
    setHasNewMessage(false);
    if (messages.length > 0) {
      setLastSeenMessageId(messages[messages.length - 1].id);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (messages.length > 0) {
      setLastSeenMessageId(messages[messages.length - 1].id);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    // Optimistically add user message
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await chatAPI.sendMessage(userMessage);
      
      // Replace temp message and add assistant response
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        const newMsgs = [
          ...filtered,
          response.data.user_message,
          response.data.assistant_message
        ];
        setLastSeenMessageId(response.data.assistant_message.id);
        return newMsgs;
      });
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      setInput(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Clear chat history?')) return;
    
    try {
      await chatAPI.clearHistory();
      setMessages([]);
      setLastSeenMessageId(null);
      toast.success('Chat cleared');
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  const handleSuggestion = (text) => {
    setInput(text);
    inputRef.current?.focus();
  };

  // Quick suggestions - fertility focused
  const suggestions = [
    'Boost my fertility',
    'Best supplements for TTC?',
    'What is epigenetics?',
  ];

  if (!user) return null;

  return (
    <>
      {/* Chat Bubble Button */}
      <button
        onClick={handleOpen}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        } bg-gradient-to-br from-[#E8A095] to-[#D68C82]`}
        data-testid="chat-bubble-btn"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        
        {/* Notification Badge */}
        {hasNewMessage && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#4A2C2A] rounded-full flex items-center justify-center animate-pulse">
            <span className="w-2 h-2 bg-white rounded-full" />
          </span>
        )}
        
        {/* Pulse ring animation */}
        <span className="absolute inset-0 rounded-full bg-[#E8A095] animate-ping opacity-20" />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          isOpen 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="w-[calc(100vw-3rem)] sm:w-[400px] h-[70vh] sm:h-[560px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-[#E8A095]/20 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#E8A095] to-[#D68C82] text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/20 flex items-center justify-center">
                <img 
                  src={DR_ERIKA_AVATAR} 
                  alt="Dr. Erika" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Dr. Erika</h3>
                <p className="text-xs text-white/80">Your Wellness Advisor • Online</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={handleClearHistory}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="chat-bubble-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FDFBF7]">
            {initialLoading ? (
              <div className="flex-1 flex items-center justify-center h-full">
                <div className="spinner" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-[#E8A095]/20 shadow-lg mb-4">
                  <img 
                    src={DR_ERIKA_AVATAR} 
                    alt="Dr. Erika" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="font-medium text-[#4A2C2A] mb-2">Hi, I'm Dr. Erika!</h4>
                <p className="text-sm text-[#4A2C2A]/60 mb-4">
                  Your personal fertility and epigenetics advisor. Ask me about conception, pregnancy nutrition, or how to optimize your baby's health.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className="px-3 py-1.5 bg-white border border-[#E8A095]/30 rounded-full text-xs text-[#4A2C2A]/70 hover:border-[#E8A095] hover:text-[#4A2C2A] transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-end gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
                        message.role === 'user' 
                          ? 'bg-[#4A2C2A]' 
                          : 'bg-gradient-to-br from-[#E8A095] to-[#D68C82]'
                      }`}>
                        {message.role === 'user' 
                          ? <User className="w-3.5 h-3.5 text-white" />
                          : <img src={DR_ERIKA_AVATAR} alt="Dr. Erika" className="w-full h-full object-cover" />
                        }
                      </div>
                      <div className={`rounded-2xl px-3 py-2 ${
                        message.role === 'user'
                          ? 'bg-[#4A2C2A] text-white rounded-br-sm'
                          : 'bg-white border border-[#E8A095]/20 text-[#4A2C2A] rounded-bl-sm shadow-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                        <p className={`text-[10px] mt-1 ${
                          message.role === 'user' ? 'text-white/50' : 'text-[#4A2C2A]/40'
                        }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {loading && (
                  <div className="flex justify-start">
                    <div className="flex items-end gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E8A095] to-[#D68C82] flex items-center justify-center overflow-hidden">
                        <img src={DR_ERIKA_AVATAR} alt="Dr. Erika" className="w-full h-full object-cover" />
                      </div>
                      <div className="bg-white border border-[#E8A095]/20 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 bg-[#E8A095] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-[#E8A095] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-[#E8A095] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <form onSubmit={handleSend} className="p-3 bg-white border-t border-[#E8A095]/10">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Dr. Erika anything..."
                disabled={loading}
                className="flex-1 bg-[#FDFBF7] border-[#E8A095]/20 focus:border-[#E8A095] rounded-xl text-sm"
                data-testid="chat-bubble-input"
              />
              <Button
                type="submit"
                disabled={!input.trim() || loading}
                className="bg-[#E8A095] hover:bg-[#D68C82] text-white rounded-xl px-4"
                data-testid="chat-bubble-send"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
